import { ReactNode, useRef } from 'react';
import c from 'classnames';
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BooleanInput, {
  BooleanInputProps,
} from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import s from './BooleanInputField.scss';

interface Props extends Omit<BooleanInputProps, 'id' | 'value' | 'onChange' | 'label'> {
  name: string;
  isRequired?: boolean;
  // both label and onChange are required on BooleanInput
  // but can be undefined on this controller wrapper
  label?: ReactNode;
  onChange?: (newValue: boolean) => void;
}

function BooleanInputField({
  name,
  label,
  details,
  isRequired,
  onChange,
  labelClassName,
  ...props
}: Props) {
  const { t } = useTranslation();
  const { current: id } = useRef(uniqueId('input'));
  const { control, errors } = useFormContext();
  const error = get(errors, name);
  const errorMessage =
    error?.type === 'required' ? t('sellerModal.error.required') : error?.message;

  const {
    field: { onChange: controllerOnChange, ref, ...inputProps },
  } = useController({
    name,
    control,
    rules: {
      required: isRequired,
    },
  });

  return (
    <BooleanInput
      id={id}
      details={error ? errorMessage : details}
      validationResult={error ? 'error' : undefined}
      label={label}
      labelClassName={c(s['label'], labelClassName)}
      {...props}
      {...inputProps}
      onChange={v => {
        controllerOnChange(v);
        onChange && onChange(v);
      }}
    />
  );
}

export default BooleanInputField;
